import { Button } from '@liveauctioneers/hammer-ui-core/button';
import { cn } from '@liveauctioneers/hammer-ui-theme/cn';
import { isEqual } from 'es-toolkit';
import { MouseEventHandler, useEffect, useMemo } from 'react';

import { BidConsoleOpener } from '@/components/BidConsoleOpener/BidConsoleOpener';
import { CatalogCardProgressBar } from '@/components/CatalogCard/CatalogCardProgressBar';
import { RouteButton } from '@/components/RouteButton/RouteButton';
import { useIsMobileLayout } from '@/hooks/useIsMobileLayout';
import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import {
    getCatalogCoverLotsTransformed,
    getCatalogIsCatalogOnly,
    getCatalogIsSEOExcluded,
    getCatalogIsTimed,
    getCatalogIsTimedPlus,
    getCatalogIsTimedPlusClosing,
    getCatalogLotsListed,
    getCatalogSellerId,
    getCatalogTitle,
    getShouldShowLive,
} from '@/redux/modules/catalog/catalogs/catalog.selectors';
import { getLiveCatalogStatus } from '@/redux/modules/console';
import { fetchCatalogPresence, fetchLiveCatalogStatusIfNeeded } from '@/redux/modules/liveCatalogStatus';
import {
    AuctionCardClickedTrigger,
    isAuctionCardClickedTrigger,
    useTrackAuctionCardClickedAnalytics,
} from '@/utils/amplitude/analytics/catalog/trackAuctionCardClicked.analytics';
import { AuctionType } from '@/utils/amplitude/analytics/core.types';
import { useAppFormatMessage } from '@/utils/intl/useAppFormatMessage';
import { getCatalogUrl } from '@/utils/urls';

import { HomepageAuctionCardBadge } from './components/HomepageAuctionCardBadge';
import { HomepageAuctionCardCollage } from './components/HomepageAuctionCardCollage';
import { HomepageAuctionCardDetails } from './components/HomepageAuctionCardDetails';
import { HomepageAuctionCardLocation } from './components/HomepageAuctionCardLocation';
import { HomepageAuctionCardMobileCollage } from './components/HomepageAuctionCardMobileCollage';

type Props = {
    catalogId: number;
    className?: string;
    lazyLoadImages?: boolean;
    position?: number;
};

const COVER_IMAGE_HEIGHT = 190;

export const HomepageAuctionCard = ({ catalogId, className, lazyLoadImages, position }: Props) => {
    const dispatch = useAppDispatch();
    const formatMessage = useAppFormatMessage();
    const isMobile = useIsMobileLayout();
    const trackAuctionCardClicked = useTrackAuctionCardClickedAnalytics();

    const isSEOExcluded = useAppSelector((state) => getCatalogIsSEOExcluded(state, catalogId));
    const sellerId = useAppSelector((state) => getCatalogSellerId(state, catalogId));
    const title = useAppSelector((state) => getCatalogTitle(state, catalogId));
    const showLive = useAppSelector((state) => getShouldShowLive(state, catalogId));
    const isTimed = useAppSelector((state) => getCatalogIsTimed(state, catalogId));
    const isTimedPlus = useAppSelector((state) => getCatalogIsTimedPlus(state, catalogId));
    const isTimedPlusClosing = useAppSelector((state) => getCatalogIsTimedPlusClosing(state, catalogId));
    const isCatalogOnly = useAppSelector((state) => getCatalogIsCatalogOnly(state, catalogId));
    const lotCount = useAppSelector((state) => getCatalogLotsListed(state, catalogId));
    const liveStatus = useAppSelector((state) => getLiveCatalogStatus(state, catalogId));

    const {
        primaryImage,
        primaryImageHighDpi,
        primaryItemTitle,
        secondaryImage,
        secondaryImageHighDpi,
        secondaryItemTitle,
        tertiaryImage,
        tertiaryImageHighDpi,
        tertiaryItemTitle,
    } = useAppSelector((state) => getCatalogCoverLotsTransformed(state, catalogId, COVER_IMAGE_HEIGHT), isEqual);

    const live = (showLive || liveStatus === 'live') && !isTimedPlus;
    const showProgress = live || (isTimedPlusClosing && isTimedPlus);
    const moreLotsCount = Math.max(lotCount - 3, 0);
    const catalogUrl = getCatalogUrl(catalogId, title);
    const rel = isSEOExcluded ? 'nofollow' : '';

    useEffect(() => {
        if (!showProgress) {
            return;
        }

        // Same live data path CatalogVisibilityWrapper uses for CatalogGrid cards
        dispatch(fetchLiveCatalogStatusIfNeeded(catalogId, sellerId));
        dispatch(fetchCatalogPresence(catalogId));
    }, [catalogId, dispatch, sellerId, showProgress]);

    const auctionType = useMemo((): AuctionType => {
        if (isTimedPlus) {
            return AuctionType.TimedPlus;
        }
        if (isCatalogOnly) {
            return AuctionType.PreviewOnly;
        }
        if (isTimed) {
            return AuctionType.Timed;
        }
        return AuctionType.Live;
    }, [isTimedPlus, isCatalogOnly, isTimed]);

    const handleCardClick: MouseEventHandler<HTMLDivElement> = (event) => {
        if (position === undefined || position === null) {
            return;
        }

        const target = event.target as HTMLElement;
        const triggerElement = target.closest('[data-track-trigger]') as HTMLElement | null;
        if (!triggerElement) {
            return;
        }

        const rawTrigger = triggerElement.dataset.trackTrigger;
        if (!rawTrigger || !isAuctionCardClickedTrigger(rawTrigger)) {
            return;
        }

        trackAuctionCardClicked({
            auctionType,
            catalogId,
            houseId: sellerId,
            lotCount,
            position,
            trigger: rawTrigger,
        });
    };

    const coverImages = [
        { alt: primaryItemTitle, src: primaryImage, srcSet: `${primaryImageHighDpi} 2x` },
        { alt: secondaryItemTitle, src: secondaryImage, srcSet: `${secondaryImageHighDpi} 2x` },
        { alt: tertiaryItemTitle, src: tertiaryImage, srcSet: `${tertiaryImageHighDpi} 2x` },
    ];

    return (
        <div
            className={cn(
                'relative flex w-full flex-col overflow-hidden rounded-md border border-border-secondary bg-background-primary',
                isMobile ? 'max-w-[280px]' : 'max-w-[381px]',
                className
            )}
            data-testid="homepage-auction-card"
            onClick={handleCardClick}
            suppressHydrationWarning
        >
            <div className="relative">
                {isMobile ? (
                    <HomepageAuctionCardMobileCollage
                        catalogUrl={catalogUrl}
                        coverImages={coverImages}
                        lazyLoadImages={lazyLoadImages}
                        moreLotsCount={moreLotsCount}
                        rel={rel}
                    />
                ) : (
                    <HomepageAuctionCardCollage
                        catalogId={catalogId}
                        catalogUrl={catalogUrl}
                        coverImages={coverImages}
                        lazyLoadImages={lazyLoadImages}
                        live={live}
                        moreLotsCount={moreLotsCount}
                        rel={rel}
                    />
                )}
                <HomepageAuctionCardBadge catalogId={catalogId} />
                <CatalogCardProgressBar
                    catalogId={catalogId}
                    className="absolute inset-x-0 bottom-0 z-[2]"
                    percent={showProgress ? undefined : 0}
                />
            </div>
            <div className={cn('flex flex-col', isMobile ? 'p-xs' : 'p-sm')}>
                <div className="flex flex-col gap-2xs">
                    <HomepageAuctionCardDetails catalogId={catalogId} />
                    <HomepageAuctionCardLocation catalogId={catalogId} />
                </div>
                {isMobile &&
                    (live ? (
                        <BidConsoleOpener catalogId={catalogId}>
                            <Button
                                className="mt-sm w-full"
                                data-testid="homepage-auction-card-mobile-bid-now"
                                data-track-trigger={AuctionCardClickedTrigger.BidNowClick}
                                size="sm"
                                style="critical"
                                variant="outlined"
                            >
                                {formatMessage(['live_auction'])}
                            </Button>
                        </BidConsoleOpener>
                    ) : (
                        <RouteButton
                            className="mt-sm w-full"
                            data-testid="homepage-auction-card-mobile-browse-lots"
                            data-track-trigger={AuctionCardClickedTrigger.ImageClick}
                            rel={rel}
                            size="sm"
                            style="primary"
                            to={catalogUrl}
                            variant="outlined"
                        >
                            {formatMessage(['catalog_card', 'homepage', 'browseLots'])}
                        </RouteButton>
                    ))}
            </div>
        </div>
    );
};
